import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { AccessibleButton, LoadingSpinner } from '../ui';

const PaymentDocumentUpload = ({ paymentId, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [documentType, setDocumentType] = useState('receipt');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error('סוג קובץ לא נתמך. אנא העלה PDF, JPG או PNG');
      return;
    }

    // Validate file size (10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('גודל הקובץ גדול מדי. מקסימום 10MB');
      return;
    }

    setFile(selectedFile);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('אנא בחר קובץ להעלאה');
      return;
    }

    if (!documentType) {
      toast.error('אנא בחר סוג מסמך');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_type', documentType);

      await axios.post(`/api/admin/payments/${paymentId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('המסמך הועלה בהצלחה');
      onUploaded();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בהעלאת המסמך');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" dir="rtl">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">העלה מסמך רשמי</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="סגור"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Document Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              סוג מסמך *
            </label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="receipt">קבלה</option>
              <option value="invoice">חשבונית</option>
            </select>
          </div>

          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="space-y-4">
                <DocumentTextIcon className="h-12 w-12 text-primary-500 mx-auto" />
                <div>
                  <div className="text-sm font-medium text-gray-900">{file.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {formatFileSize(file.size)}
                  </div>
                </div>
                <button
                  onClick={() => setFile(null)}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  הסר קובץ
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <DocumentArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto" />
                <div>
                  <label className="cursor-pointer">
                    <span className="text-primary-600 hover:text-primary-800 font-medium">
                      לחץ לבחירת קובץ
                    </span>
                    <span className="text-gray-600"> או גרור לכאן</span>
                  </label>
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                </div>
                <div className="text-xs text-gray-500">
                  PDF, JPG או PNG (מקסימום 10MB)
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6">
            <AccessibleButton
              variant="secondary"
              onClick={onClose}
              disabled={uploading}
            >
              ביטול
            </AccessibleButton>
            <AccessibleButton
              variant="primary"
              onClick={handleUpload}
              disabled={!file || uploading}
              loading={uploading}
            >
              העלה מסמך
            </AccessibleButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDocumentUpload;
